/**
 * codex.md v4 — chart data adapters.
 *
 * Every chart on the Financial page is fed by one of the functions below.
 * They are pure, side-effect-free, and never touch React. The contract is:
 *
 *   API response  →  useMemo(() => toXYZ(api), [api])  →  <Chart data={rows} />
 *
 * No chart component should compute inside render; no adapter should
 * inspect `window`, React state, or fetch. That keeps the page's
 * top-level useMemo list the single source of truth for data shape.
 *
 * Every adapter returns data in the exact shape the target library
 * consumes:
 *
 *   Tremor <LineChart> / <BarChart>:
 *     [{ month: 'Jan', series_a: n, series_b: n, ... }, ...]
 *
 *   Tremor <BarList>:
 *     [{ name, value, href? }, ...]
 *
 *   Nivo <ResponsiveBar> (stacked):
 *     [{ month: 'Jan', fulltime: n, talentHub: n, nonBillable: n }, ...]
 *
 *   Nivo <ResponsiveHeatMap>:
 *     [{ id: 'Consultant A', data: [{ x: 'Jan', y: 80 }, ...] }, ...]
 */

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─────────────────────────────────────────────────────────────────────
// Revenue / Cost / Profit
// ─────────────────────────────────────────────────────────────────────

/**
 * monthlyFin = response of GET /financial-status/monthly for one year.
 *   [{ month: 1, revenue: { actual, actualPending, expected, forecast }, cost: { actual, expected, forecast }, profit: {...} }, ...]
 * Output is 12 rows, one per month, aligned to MONTH_LABELS.
 */
export function toRevenueRows(monthlyFin) {
  const byMonth = mapByMonth(monthlyFin);
  return MONTH_LABELS.map((label, i) => {
    const r = byMonth.get(i + 1);
    const paid = num(r?.revenue?.actual);
    const pending = num(r?.revenue?.actualPending);
    return {
      month: label,
      Actual:   paid + pending,
      Expected: num(r?.revenue?.expected),
      Forecast: num(r?.revenue?.forecast),
    };
  });
}

export function toCostRows(monthlyFin) {
  const byMonth = mapByMonth(monthlyFin);
  return MONTH_LABELS.map((label, i) => {
    const r = byMonth.get(i + 1);
    return {
      month: label,
      Actual:   num(r?.cost?.actual),
      Expected: num(r?.cost?.expected),
      Forecast: num(r?.cost?.forecast),
    };
  });
}

export function toProfitRows(monthlyFin) {
  const byMonth = mapByMonth(monthlyFin);
  return MONTH_LABELS.map((label, i) => {
    const r = byMonth.get(i + 1);
    return {
      month: label,
      Actual:   num(r?.profit?.actual),
      Expected: num(r?.profit?.expected),
      Forecast: num(r?.profit?.forecast),
    };
  });
}

// ─────────────────────────────────────────────────────────────────────
// Monthly Billing (Tremor BarChart, grouped)
// ─────────────────────────────────────────────────────────────────────

/**
 * billingData comes from the existing useMemo on FinancialStatusPage —
 * shape: [{ m: 'January', contract, forecast, actual, target, mondaySourced }, ...]
 */
export function toBillingBarRows(billingData) {
  return (billingData || []).map((row) => ({
    month:    shortMonth(row.m),
    Contract: num(row.contract),
    Forecast: num(row.forecast),
    Actual:   num(row.actual),
    Target:   num(row.target),
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Profit Margin % (Tremor LineChart)
// ─────────────────────────────────────────────────────────────────────

/**
 * profitMargin comes from the EXTENDED_FIN_DATA.profitMargin constant:
 *   { actual: [...12], forecast: [...12], target: 25 }
 */
export function toProfitMarginRows(profitMargin) {
  const a = profitMargin?.actual || [];
  const f = profitMargin?.forecast || [];
  const target = num(profitMargin?.target);
  return MONTH_LABELS.map((label, i) => ({
    month:    label,
    Actual:   num(a[i]),
    Forecast: num(f[i]),
    Target:   target,
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Utilisation Trend (Tremor LineChart)
// ─────────────────────────────────────────────────────────────────────

/**
 * trackerMonthlyActual: 12-length array of % values (null for months
 *   without tracker data). utilStatic: FINANCIAL_DATA.utilisation
 *   (actual, forecast). utilTarget: scalar.
 */
export function toUtilisationRows(trackerMonthlyActual, utilStatic, utilTarget) {
  const tracker = Array.isArray(trackerMonthlyActual) ? trackerMonthlyActual : [];
  const staticActual = utilStatic?.actual || [];
  const forecast = utilStatic?.forecast || [];
  const target = num(utilTarget);
  return MONTH_LABELS.map((label, i) => ({
    month:    label,
    Actual:   tracker[i] != null ? num(tracker[i]) : num(staticActual[i]),
    Forecast: num(forecast[i]),
    Target:   target,
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Avg Hourly Rate (Tremor LineChart)
// ─────────────────────────────────────────────────────────────────────

/**
 * rateData: FINANCIAL_DATA.avgHourlyRate { actual:[12], forecast:[12] }.
 */
export function toHourlyRateRows(rateData, rateTarget) {
  const a = rateData?.actual || [];
  const f = rateData?.forecast || [];
  const target = num(rateTarget);
  return MONTH_LABELS.map((label, i) => ({
    month:    label,
    Actual:   num(a[i]),
    Forecast: num(f[i]),
    Target:   target,
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Headcount (Nivo stacked bar)
// ─────────────────────────────────────────────────────────────────────

/**
 * headcountSeries: { fulltime:[12], talentHub:[12], nonBillables:[12] }.
 */
export function toHeadcountRows(headcountSeries) {
  const ft = headcountSeries?.fulltime || [];
  const th = headcountSeries?.talentHub || [];
  const nb = headcountSeries?.nonBillables || [];
  return MONTH_LABELS.map((label, i) => ({
    month:       label,
    fulltime:    num(ft[i]),
    talentHub:   num(th[i]),
    nonBillable: num(nb[i]),
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Waterfall (Nivo ResponsiveBar with grouped mode)
// ─────────────────────────────────────────────────────────────────────

/**
 * waterfallData = existing useMemo: [{ m, revenue, costs }, ...]
 * Nivo waterfall is approximated by grouped bars per month: revenue +,
 * cost -. FY-net rendered as a marker on the chart.
 */
export function toWaterfallRows(waterfallData) {
  return (waterfallData || []).map((row) => ({
    month:   shortMonth(row.m),
    Revenue: num(row.revenue),
    Cost:   -num(row.costs),
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Utilisation Heatmap (Nivo ResponsiveHeatMap)
// ─────────────────────────────────────────────────────────────────────

/**
 * heatmapApi: response from GET /api/consultants/utilisation-heatmap
 *   [{ id: 'Consultant name', data: [{ x: 'Jan', y: 80 }, ...] }, ...]
 * Passed through as-is (server already returns the Nivo shape). Guards
 * against null / malformed rows.
 */
export function toHeatmapRows(heatmapApi) {
  if (!Array.isArray(heatmapApi)) return [];
  return heatmapApi
    .filter((r) => r && r.id && Array.isArray(r.data))
    .map((r) => ({
      id: String(r.id),
      data: r.data.map((pt) => ({ x: String(pt?.x ?? ''), y: num(pt?.y) })),
    }));
}

// ─────────────────────────────────────────────────────────────────────
// Top Clients (Tremor BarList)
// ─────────────────────────────────────────────────────────────────────

/**
 * apiRows: response from /financial-status/top-clients — already
 * aggregated + sorted + currency-converted server-side. Shape:
 *   [{ clientName: 'Acme AB', totalInvoiced: 1240000 }, ...]
 * Adapter just renames fields for Tremor's BarList. Kept pure so
 * unit-testing the shape is trivial.
 */
export function toTopClientsBars(apiRows) {
  if (!Array.isArray(apiRows) || apiRows.length === 0) return [];
  return apiRows
    .filter((r) => r && r.clientName && num(r.totalInvoiced) > 0)
    .map((r) => ({ name: r.clientName, value: num(r.totalInvoiced) }));
}

// ─────────────────────────────────────────────────────────────────────
// Pipeline (Tremor BarList) — scoping deal value by stage
// ─────────────────────────────────────────────────────────────────────

/**
 * scoping: array of Scoping items (the usual app state). Groups
 * dealValue by stage and returns top entries sorted by value.
 */
export function toPipelineBars(scoping) {
  if (!Array.isArray(scoping) || scoping.length === 0) return [];
  const byStage = new Map();
  for (const s of scoping) {
    if (!s || !s.stage) continue;
    const label = typeof s.stage === 'string' ? s.stage : String(s.stage);
    const v = num(s.dealValue);
    const prev = byStage.get(label) || 0;
    byStage.set(label, prev + v);
  }
  return Array.from(byStage.entries())
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function mapByMonth(rows) {
  const m = new Map();
  if (!Array.isArray(rows)) return m;
  for (const r of rows) if (r && typeof r.month === 'number') m.set(r.month, r);
  return m;
}

function num(v) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function shortMonth(m) {
  if (!m) return '';
  const s = String(m);
  const mo = s.slice(0, 3);
  return MONTH_LABELS.includes(mo) ? mo : s;
}
